<script setup lang="ts">
import type { Chart } from 'highcharts';
import { computed, toRef } from 'vue';
import { RotateCcw } from 'lucide-vue-next';
import GenericChart from '@/components/charts/GenericChart.vue';
import { Button } from '@/components/ui/button';
import { i18n } from '@/plugins/internationalization/i18n';
import type { RecordWeekBin } from '@/composables/records/useRecordsAggregation';
import { clearHoverBand, useRecordsChartOptions } from '@/composables/records/useRecordsChartOptions';

/*
 * The weekly column chart the records pages navigate their table with: one bar per week, click to
 * select, click again to clear. A title and a colour are all that differ between those pages, so a
 * new page adds a heading and a colour token rather than another copy of this.
 *
 * The tooltip names what a bar counts, not which registration type produced it - the page heading
 * above already says that - so the wording is the same on every records page and is resolved here.
 */
const props = defineProps<{
    bins: RecordWeekBin[];
    selectedWeekStart: string | null;
    /** Hex of the series colour, from the `chart` token block. */
    color: string;
    /**
     * What the bars count, in the page's own words - the page heading above names the registration
     * type, not the measure. Carried in the chart's own title rather than in a heading beside it,
     * so it follows the plot into the expand modal and onto a print, where nothing else names it.
     */
    title: string;
    /**
     * What one unit of a bar is, named in the tooltip, already translated; defaults to the records
     * pages' own wording, pigs, for the pages that count those.
     */
    seriesName?: string;
    /** More about a week, after its value in the tooltip. See `RecordsChartConfig`. */
    pointDetail?: (weekStart: string) => string | null;
}>();

// Computed rather than a constant so the tooltip follows a language switch.
const seriesName = computed(() => props.seriesName ?? i18n.global.t('views.pigs.records.chart.seriesName'));

const emit = defineEmits<{
    'week-selected': [weekStart: string];
    'selection-reset': [];
}>();

const options = useRecordsChartOptions(toRef(props, 'bins'), toRef(props, 'selectedWeekStart'), {
    color: toRef(props, 'color'),
    title: toRef(props, 'title'),
    seriesName,
    pointDetail: (weekStart: string) => props.pointDetail?.(weekStart) ?? null,
    onBinClicked: (weekStart: string) => emit('week-selected', weekStart),
});

function onChartUpdate(instance: Chart) {
    // A redraw drops imperatively added bands; the next pointer move puts a fresh one back.
    clearHoverBand(instance);
}
</script>

<template>
    <!-- GenericChart declares `options` as a ComputedRef, which the template unwraps before it is
         checked; every chart in the app casts here for the same reason. -->
    <GenericChart :options="(options as any)" @update="onChartUpdate">
        <template #prepend-actions>
            <!-- The page's own controls, if any, ahead of the reset. -->
            <slot name="prepend-actions" />
            <Button variant="outline" size="sm" :disabled="selectedWeekStart === null" :aria-label="$t('views.pigs.records.chart.resetSelectionHint')" @click="emit('selection-reset')">
                <RotateCcw class="size-4 mr-1.5" aria-hidden="true" />
                {{ $t('views.pigs.records.chart.resetSelection') }}
            </Button>
        </template>
    </GenericChart>
</template>
